import { useQuery } from 'react-query'
import { useAuth0 } from '../../contexts/auth0-context'
import { fetchAndEnrichAllPubMedArticles } from '../../components/Evidence/APIs/PubMed/helpers/pubmedFetchAll'
import type { FetchAllPubMedArticlesResult } from '../../components/Evidence/APIs/PubMed/helpers/pubmedFetchAll'
import type { PubMedSearchParams } from '../../components/Evidence/APIs/PubMed/types'

/**
 * Fetches and enriches (scores) the full set of PubMed articles for every id in `ids`
 * (chunked efetch calls under the hood), caching by the exact id list so repeat renders
 * for the same search don't re-fetch. Sorting is intentionally NOT done here - callers
 * sort the returned `articles` array in memory per sort mode.
 *
 * @param ids - The full PMID list for the current search (e.g. from esearch)
 */
export const useFetchAllPubMedArticles = (ids: string[], params: PubMedSearchParams | null) => {
    const { getTokenSilently } = useAuth0()

    return useQuery<FetchAllPubMedArticlesResult>(
        [
            'pubmedAllArticles',
            ids,
            {
                diseaseArea: params?.diseaseArea || '',
                interventions: params?.interventions || '',
                sponsor: params?.sponsor || '',
                outcomes: params?.outcomes || '',
                authors: params?.authors || '',
                startDate: params?.startDate?.format('YYYY-MM') || '',
                endDate: params?.endDate?.format('YYYY-MM') || '',
                articleTypes: params?.articleTypes || {},
                openAccessOnly: params?.openAccessOnly || false,
                retmax: params?.retmax || 0,
            },
        ],
        async () => {
            const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })
            return fetchAndEnrichAllPubMedArticles(ids, params as PubMedSearchParams, token)
        },
        {
            enabled: ids.length > 0 && !!params,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        }
    )
}
